import React from 'react';

// Last measured auth-card height, kept at module scope so it survives the
// /login ⇄ /register route swap (each route unmounts the other). The newly
// mounted page seeds its card at the previous page's height, then measures its
// own content and lets the CSS height transition morph from the old height to
// the new one. `null` until the first card is ever measured — a cold load
// animates nothing, it just renders at its natural height.
let lastAuthCardHeight: number | null = null;

export type AuthCardMorph = {
  /**
   * Attach to the padded inner wrapper whose natural height drives the card. A
   * callback ref (not a plain object ref) so the ResizeObserver re-binds to
   * whatever node is currently mounted — see the note in {@link useAuthCardMorph}
   * on the `?link=` return path, where the wrapper unmounts (PageLoader) and
   * remounts as a brand-new node.
   */
  innerRef: React.RefCallback<HTMLDivElement>;
  /** Inline style for the `.body` card carrying its (animated) height. */
  cardStyle: React.CSSProperties;
};

/**
 * Drives the morphing height of the sign-in / sign-up card. The card (`.body`)
 * is given an explicit pixel height equal to its content's natural height
 * (measured on the padded `.body-inner` wrapper, so the value already includes
 * the card padding) plus a CSS height transition — so it grows / shrinks
 * smoothly when the user navigates between `/login` and `/register`, and when the
 * form swaps to the "email sent" view.
 *
 * Height is seeded from the previous page's measurement (module scope) so the
 * cross-route change animates from the real previous height instead of snapping.
 * The measure runs in a post-paint `requestAnimationFrame` (not a layout effect)
 * on purpose: the card must first paint at the seeded height for the height
 * transition to fire — a synchronous pre-paint set would change the height before
 * the first paint and skip the animation. A `ResizeObserver` then keeps the
 * height in sync as the inner content changes (form ↔ email-sent).
 *
 * The observer is bound via a CALLBACK ref (not a `useEffect([])` reading an object
 * ref) so it re-attaches to whatever `.body-inner` node is actually mounted. This
 * matters on the `?link=` return path: the page renders the form, flips to the
 * full-page `PageLoader` while the link is redeemed, then (on a failure such as an
 * expired link) flips back to the form — a NEW inner node. A one-shot effect binds
 * only to the first, transient node; when that detaches its `scrollHeight` reads 0,
 * which would latch the card to `0px` (and, with `.body { overflow: hidden }`,
 * collapse the whole card to nothing). The callback ref rebinds to the live node,
 * and `measure` ignores a 0 reading so a detaching node can never latch the collapse.
 */
export const useAuthCardMorph = (): AuthCardMorph => {
  const [height, setHeight] = React.useState<number | null>(lastAuthCardHeight);
  const cleanupRef = React.useRef<(() => void) | null>(null);

  const innerRef = React.useCallback<React.RefCallback<HTMLDivElement>>((el) => {
    // Tear down the observer/raf bound to any previous node before (re)binding.
    cleanupRef.current?.();
    cleanupRef.current = null;

    if (el === null) {
      return;
    }

    const measure = (): void => {
      const next = el.scrollHeight;
      // A detached or not-yet-laid-out node measures 0; ignore it so a transient
      // unmount (the PageLoader swap) never latches the card to a collapsed 0px.
      if (next === 0) {
        return;
      }
      lastAuthCardHeight = next;
      setHeight(next);
    };

    // After the first paint (at the seeded height) → triggers the morph.
    const raf = requestAnimationFrame(measure);
    // Keeps the card height in sync when its content changes height in place.
    const observer = new ResizeObserver(measure);
    observer.observe(el);

    cleanupRef.current = () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, []);

  return {
    innerRef,
    cardStyle: { height: height === null ? 'auto' : height },
  };
};
